/* eslint class-methods-use-this: 0 */
import { some } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import SubscriptionApi from 'client/api/v2/SubscriptionApi';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import Util from 'client/util/Util';

const {
  getProductCodeByType,
  FeatureGroups,
  SubscriptionFields,
  SubscriptionTypes,
} = SubscriptionConstants;
const {
  listPrice: listPriceField,
  productCode: productCodeField,
} = SubscriptionFields;
const { fixDecimals } = Util;

export class SubscriptionCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    module.getters.getIsDefaultComponent = (...params) => this
      .getIsDefaultComponent(...params);
    module.getters.getOptionalAddOns = (...params) => this
      .getOptionalAddOns(...params);
    module.getters.getSubscription = (...params) => this
      .getSubscription(...params);
    module.getters.getSubscriptionObjects = (...params) => this
      .getSubscriptionObjects(...params);
    module.getters.getSubscriptionPrice = (...params) => this
      .getSubscriptionPrice(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------
  getIsDefaultComponent(state) {
    return (subscriptionProductCode, componentProductCode) => {
      const subscription = state.entities.items.byId[subscriptionProductCode];
      return some(subscription?.components, ['productCode', componentProductCode]);
    };
  }

  getSubscriptionPrice(state, getters, rootState, rootGetters) {
    const subscriptionIsAnnual = rootGetters['PricingModule/getIsAnnual'];

    return (productCode) => {
      const subscription = state.entities.items.byId[productCode];
      if (!subscription) return null;
      const { annualPrice, listPrice, components } = subscription;

      // Display price as base price of the subscription, plus the price of any
      // default components
      let price = subscriptionIsAnnual ? annualPrice : listPrice;
      if (components) {
        components.forEach((component) => {
          price += subscriptionIsAnnual ? component.annualPrice : component.listPrice;
        });
      }

      return fixDecimals(price);
    };
  }

  getOptionalAddOns(state) {
    return (productCode) => {
      const subscription = state.entities.items.byId[productCode];
      const features = Object.values(FeatureGroups).map((obj) => obj.features).flat();

      const orderedOptionalAddOnsFromFeaturePricingMatrix = features.reduce((newArr, feature) => {
        subscription.optionalAddOns
          .forEach((optionalAddOn) => {
            if (feature.name === optionalAddOn.features[0].name) {
              newArr.push(optionalAddOn);
            }
          });
        return newArr;
      }, []);

      const nonDependentAddOns = orderedOptionalAddOnsFromFeaturePricingMatrix
        .filter(addOn => !addOn.requires);

      const dependentAddOns = orderedOptionalAddOnsFromFeaturePricingMatrix
        .filter(addOn => addOn.requires);

      return [...nonDependentAddOns, ...dependentAddOns];
    };
  }

  getSubscription(state) {
    return (productCode) => state.entities.items.byId[productCode];
  }

  getSubscriptionObjects(state) {
    const {
      sso,
      coreDirectory,
      platform,
      platformPlus,
    } = SubscriptionTypes;

    const subscriptionsById = state.entities.items.byId;
    return {
      sso: subscriptionsById[getProductCodeByType(sso)],
      coreDirectory: subscriptionsById[getProductCodeByType(coreDirectory)],
      platform: subscriptionsById[getProductCodeByType(platform)],
      platformPlus: subscriptionsById[getProductCodeByType(platformPlus)],
    };
  }
}

export default new SubscriptionCollectionClass(SubscriptionApi, productCodeField, listPriceField)
  .extractModule();
